// Parses a pasted sample cXML request and extracts template field mapping
// values. Each supported request type has its own extractor; add new ones to
// parsersByRequestType as templates grow.

const getText = (el) => (el?.textContent ?? "").trim();

const getAttr = (el, attr, values, fieldName) => {
  const value = el?.getAttribute(attr);
  if (value !== null && value !== undefined) values[fieldName] = value;
};

// Extracts a cXML <Address> block (inside ShipTo/BillTo) into dotted field
// names under the given prefix. Values not in the active template's field
// list are filtered out downstream, so over-extraction is harmless.
function extractAddressBlock(containerEl, prefix, values) {
  const address = containerEl?.getElementsByTagName("Address")[0];
  if (!address) return;
  getAttr(address, "addressID", values, `${prefix}.Address.@addressID`);
  const nameEl = address.getElementsByTagName("Name")[0];
  if (nameEl) {
    values[`${prefix}.Address.Name`] = getText(nameEl);
    getAttr(nameEl, "xml:lang", values, `${prefix}.Address.Name.@xml:lang`);
  }
  const postal = address.getElementsByTagName("PostalAddress")[0];
  if (postal) {
    getAttr(postal, "name", values, `${prefix}.Address.PostalAddress.@name`);
    ["DeliverTo", "Street", "City", "State", "PostalCode"].forEach((tag) => {
      const el = postal.getElementsByTagName(tag)[0];
      if (el) values[`${prefix}.Address.PostalAddress.${tag}`] = getText(el);
    });
    const country = postal.getElementsByTagName("Country")[0];
    if (country) {
      values[`${prefix}.Address.PostalAddress.Country`] = getText(country);
      getAttr(country, "isoCountryCode", values, `${prefix}.Address.PostalAddress.Country.@isoCountryCode`);
    }
  }
  const email = address.getElementsByTagName("Email")[0];
  if (email) values[`${prefix}.Address.Email`] = getText(email);
  const phone = address.getElementsByTagName("Phone")[0]?.getElementsByTagName("TelephoneNumber")[0];
  if (phone) {
    const countryCode = phone.getElementsByTagName("CountryCode")[0];
    if (countryCode) {
      values[`${prefix}.Address.Phone.TelephoneNumber.CountryCode`] = getText(countryCode);
      getAttr(countryCode, "isoCountryCode", values, `${prefix}.Address.Phone.TelephoneNumber.CountryCode.@isoCountryCode`);
    }
    ["AreaOrCityCode", "Number"].forEach((tag) => {
      const el = phone.getElementsByTagName(tag)[0];
      if (el) values[`${prefix}.Address.Phone.TelephoneNumber.${tag}`] = getText(el);
    });
  }
}

function parsePunchOutSetupRequest(doc) {
  const values = {};
  const setupRequest = doc.getElementsByTagName("PunchOutSetupRequest")[0];
  if (!setupRequest) {
    return { values, error: "No <PunchOutSetupRequest> element found in the sample." };
  }

  // DeploymentMode comes from the parent <Request deploymentMode='...'>
  getAttr(doc.getElementsByTagName("Request")[0], "deploymentMode", values, "DeploymentMode");

  // Extrinsics: <Extrinsic name='UserEmail'>...</Extrinsic> -> Extrinsic.UserEmail
  [...setupRequest.getElementsByTagName("Extrinsic")].forEach((el) => {
    const name = el.getAttribute("name");
    if (name) values[`Extrinsic.${name}`] = getText(el);
  });

  // ShipTo address block
  extractAddressBlock(setupRequest.getElementsByTagName("ShipTo")[0], "ShipTo", values);

  // Only the runbook id is read from the BrowserFormPost URL
  // (https://host/v1/orgs/{clientId}/messages/{runbookId}?accesstoken={token})
  // and only to preselect the runbook picker when it matches. The other
  // Callback values are not taken from samples: ClientId/ClientGuid derive
  // from context and AccessToken is always entered manually.
  const url = getText(
    setupRequest.getElementsByTagName("BrowserFormPost")[0]?.getElementsByTagName("URL")[0]
  );
  if (url) {
    const messageMatch = url.match(/\/messages\/([^/?#]+)/i);
    if (messageMatch) values["Callback.RunbookId"] = messageMatch[1];
  }

  return { values };
}

function parseOrderRequest(doc) {
  const values = {};
  const header = doc.getElementsByTagName("OrderRequestHeader")[0];
  if (!header) {
    return { values, error: "No <OrderRequestHeader> element found in the sample." };
  }

  // DeploymentMode comes from the parent <Request deploymentMode='...'> when
  // the sample includes it (fragments usually start at OrderRequestHeader)
  getAttr(doc.getElementsByTagName("Request")[0], "deploymentMode", values, "DeploymentMode");

  getAttr(header, "orderID", values, "OrderRequestHeader.@orderID");
  getAttr(header, "orderDate", values, "OrderRequestHeader.@orderDate");
  getAttr(header, "type", values, "OrderRequestHeader.@type");

  const money = header.getElementsByTagName("Total")[0]?.getElementsByTagName("Money")[0];
  if (money) {
    values["OrderRequestHeader.Total.Money"] = getText(money);
    getAttr(money, "currency", values, "OrderRequestHeader.Total.Money.@currency");
  }

  extractAddressBlock(header.getElementsByTagName("ShipTo")[0], "OrderRequestHeader.ShipTo", values);
  extractAddressBlock(header.getElementsByTagName("BillTo")[0], "OrderRequestHeader.BillTo", values);

  const comments = header.getElementsByTagName("Comments")[0];
  if (comments) values["OrderRequestHeader.Comments"] = getText(comments);

  // ItemOut is intentionally not parsed — it's entered manually as a raw
  // well-formed XML string

  return { values };
}

const parsersByRequestType = {
  PunchOutSetupRequest: parsePunchOutSetupRequest,
  OrderRequest: parseOrderRequest,
};

export function parsePunchOutSample(xmlText, requestType) {
  const parser = parsersByRequestType[requestType];
  if (!parser) {
    return { values: {}, error: `No sample parser is available for ${requestType}.` };
  }
  if (!xmlText?.trim()) {
    return { values: {}, error: "Paste a sample request first." };
  }
  let doc = new DOMParser().parseFromString(xmlText, "text/xml");
  if (doc.getElementsByTagName("parsererror").length > 0) {
    // Samples are often fragments with sibling roots (e.g. an
    // OrderRequestHeader followed by ItemOut elements) — retry wrapped in a
    // synthetic root before giving up
    doc = new DOMParser().parseFromString(`<sample>${xmlText}</sample>`, "text/xml");
    if (doc.getElementsByTagName("parsererror").length > 0) {
      return { values: {}, error: "The sample is not valid XML." };
    }
  }
  return parser(doc);
}
